import type { ReactNode } from 'react';
import { telLink, whatsappLink } from '@/lib/contact';
import { useT } from '@/i18n/use-t';
import { MailIcon, PhoneIcon } from '@/components/icons';
import { cx } from '@/components/ui';

/**
 * WhatsApp, call, email — the ways to reach a customer, at the head of their
 * page because a support call is what it is open for. Each appears only when
 * there is something to reach them on; a number that is not one gets neither
 * WhatsApp nor a call rather than a link that dials nothing.
 */
export function ContactLinks({ phone, email }: { phone: string | null; email: string | null }) {
  const t = useT();
  const whatsapp = whatsappLink(phone);
  const call = telLink(phone);

  return (
    <>
      {whatsapp ? (
        <ContactLink href={whatsapp} external className="text-success">
          <WhatsappGlyph />
          {t('contactWhatsapp')}
        </ContactLink>
      ) : null}
      {call ? (
        <ContactLink href={call}>
          <PhoneIcon className="size-4" />
          {t('contactCall')}
        </ContactLink>
      ) : null}
      {email ? (
        <ContactLink href={`mailto:${email}`}>
          <MailIcon className="size-4" />
          {t('contactEmail')}
        </ContactLink>
      ) : null}
    </>
  );
}

/** A link that sits among the header's buttons and looks like one of them. */
function ContactLink({
  href,
  external,
  className,
  children,
}: {
  href: string;
  external?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
      className={cx(
        'inline-flex min-h-10 items-center justify-center gap-2 rounded-control border border-line bg-surface px-4 text-sm font-medium whitespace-nowrap transition-all hover:bg-surface-sunken',
        className ?? 'text-ink',
      )}
    >
      {children}
    </a>
  );
}

function WhatsappGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-4" fill="currentColor">
      <path d="M12 2a10 10 0 0 0-8.66 15l-1.3 4.74 4.86-1.27A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-2.88.75.77-2.8-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.14c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.56.13-.16.24-.64.8-.79.96-.14.17-.29.19-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.7-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.15.16-.25.25-.41.08-.17.04-.31-.02-.44-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.41-.56-.42h-.48a.92.92 0 0 0-.67.31c-.23.25-.88.86-.88 2.09s.9 2.43 1.03 2.6c.12.16 1.77 2.7 4.29 3.79.6.26 1.07.41 1.43.53.6.19 1.15.16 1.58.1.48-.07 1.46-.6 1.67-1.18.2-.58.2-1.07.14-1.18-.06-.1-.22-.16-.47-.29Z" />
    </svg>
  );
}
